import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Warning, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { log } from "@/lib/logger";

const CONFIRM_WORD = "RESILIER";

export default function CancelSubscriptionDialog({ open, onOpenChange, onCancelled }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setTyped(""); setBusy(false); } }, [open]);

  const canConfirm = typed.trim().toUpperCase() === CONFIRM_WORD && !busy;

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Résiliation programmée pour la fin de période");
      onCancelled?.();
      onOpenChange(false);
    } catch (err) {
      log.error("cancel", err);
      toast.error(err?.response?.data?.detail || "Résiliation impossible");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1E3A5F] flex items-center gap-2">
            <XCircle size={22} weight="duotone" className="text-[#B9502C]" /> Résilier mon abonnement
          </DialogTitle>
          <DialogDescription className="text-sm text-[#5F758F]">
            Vous conservez l'accès jusqu'à la fin de la période déjà payée.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-[#FBE8E1] border-2 border-[#E27C4F] rounded-[10px] p-4 flex items-start gap-3">
          <Warning size={22} weight="duotone" className="text-[#B9502C] shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-[#8A3E20]">Attention — action irréversible</p>
            <p className="text-[#8A3E20] mt-1 leading-relaxed">
              À l'issue de la période en cours, votre abonnement sera clôturé et{" "}
              <strong>toutes les données du cabinet (patients, envois, réponses, questionnaires) seront définitivement effacées</strong>{" "}
              conformément à notre politique RGPD. Cette suppression est irrécupérable.
            </p>
            <p className="text-[#8A3E20] mt-2 leading-relaxed">
              Nous vous conseillons d'exporter au préalable vos données depuis l'onglet <em>RGPD</em> de chaque fiche patient.
            </p>
          </div>
        </div>

        <div>
          <label className="text-xs tracking-[0.14em] uppercase text-[#5F758F] font-medium block mb-2">
            Pour confirmer, saisissez <span className="font-bold text-[#B9502C]">{CONFIRM_WORD}</span> ci-dessous
          </label>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={CONFIRM_WORD}
            autoComplete="off"
            className="bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#B9502C] uppercase tracking-widest"
            data-testid="cancel-confirm-input"
            disabled={busy}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-[10px] font-semibold"
            data-testid="cancel-abort-btn"
          >
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!canConfirm}
            className="rounded-[10px] bg-[#B9502C] hover:bg-[#8A3E20] text-white font-semibold transition-colors disabled:opacity-40"
            data-testid="cancel-confirm-btn"
          >
            <XCircle size={16} className="mr-2" />
            {busy ? "Résiliation…" : "Résilier définitivement"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
